// A tenant's rent month, for defaulting the invoice form.
//
// Mirrors cyclePeriodFor / dueDateForCycle in backend/utils/rentPeriod.js. The server is the
// authority on what gets billed; these only pre-fill the form so the accountant sees the
// tenant's own month (10th to 9th for someone who moved in on the 10th) rather than typing
// it from memory. Dates are plain 'YYYY-MM-DD' strings throughout, as the date inputs want.

const pad = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const clampDay = (day, year, month) => {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const wanted = parseInt(day, 10);
  if (!Number.isInteger(wanted)) return 1;
  return Math.min(Math.max(wanted, 1), daysInMonth);
};

/** 1–31, or 1 when unset — day 1 is the calendar month. */
export const normaliseCycleDay = (day) => {
  const n = parseInt(day, 10);
  return Number.isInteger(n) && n >= 1 && n <= 31 ? n : 1;
};

/** The rent month containing `date` for a tenant whose cycle starts on `cycleDay`. */
export const cyclePeriodFor = (cycleDay, date = new Date()) => {
  const cycle = normaliseCycleDay(cycleDay);
  let year = date.getFullYear();
  let month = date.getMonth();
  if (date.getDate() < clampDay(cycle, year, month)) {
    month -= 1;
    if (month < 0) { month = 11; year -= 1; }
  }
  const start = new Date(year, month, clampDay(cycle, year, month));
  const nextYear = month === 11 ? year + 1 : year;
  const nextMonth = (month + 1) % 12;
  const nextStart = new Date(nextYear, nextMonth, clampDay(cycle, nextYear, nextMonth));
  const end = new Date(nextStart.getFullYear(), nextStart.getMonth(), nextStart.getDate() - 1);
  return { periodStart: iso(start), periodEnd: iso(end) };
};

/** When rent for a month starting `periodStart` falls due, given the tenant's pay day. */
export const dueDateForCycle = (periodStart, paymentDay) => {
  const start = new Date(periodStart);
  if (Number.isNaN(start.getTime())) return '';
  const pay = normaliseCycleDay(paymentDay);
  const sameMonth = new Date(
    start.getFullYear(), start.getMonth(), clampDay(pay, start.getFullYear(), start.getMonth())
  );
  if (sameMonth >= start) return iso(sameMonth);
  const nextYear = start.getMonth() === 11 ? start.getFullYear() + 1 : start.getFullYear();
  const nextMonth = (start.getMonth() + 1) % 12;
  return iso(new Date(nextYear, nextMonth, clampDay(pay, nextYear, nextMonth)));
};
